import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import type {
    DynamicImage,
    ImagePlaceholderElement,
    WordCloudConfig
} from '@/typings/customilyElements/canvasElements';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption } from '../types';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { uploadImageToS3 } from '@/utils/imageUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShapeProperties',
  props: {
    imageElement: null,
    editMode: { type: Boolean }
  },
  emits: ["open-shape-library-selector", "trigger-loading"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-shape-library-selector'): void;
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
};

const IMAGE_TYPE = LibraryTypeEnum.Image;





const model = reactive({
    shapeOption: 0 as number | undefined,
    shapeMap: new Map<number, DynamicImage>(),
    shapeLibraryId: undefined as number | undefined
});

const disabled = computed(() => !!props.imageElement?.locked);

const initShapeMap = (images: [number, string][]) => {
    const newMap = new Map<number, DynamicImage>();
    for (const [position, image] of images) {
        if (!image) continue;

        newMap.set(position, { path: image, fileName: '' });
    }
    model.shapeMap = new Map(newMap);
};

const updateOptionSelected = async (option: number, value?: ElementMapOption) => {
    const element = props.imageElement;
    if (!element) return;

    const image = typeof value == 'string' ? value : (value as DynamicImage)?.path || model.shapeMap.get(option)?.path;
    model.shapeOption = element.dynamicImageVariation = option;
    element.testingShapeUrl = image;
};

watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordCloudConfig) return;

        const config = element.wordCloudConfig;
        model.shapeOption = element.dynamicImageVariation || 1;
        model.shapeLibraryId = config.libraryId;
        initShapeMap(config.shapeMap);
        if (!model.shapeLibraryId) updateOptionSelected(model.shapeOption);
    },
    { immediate: true }
);

const wordCloud = computed(() => props.imageElement?.wordCloudConfig);

const displayLoader = (active = true, message?: string) => emit('trigger-loading', active, message);

const onImageUpload = async (files: File[], position?: number) => {
    const element = props.imageElement;
    if (!element) return;

    let imgPosition = position || model.shapeMap.size + 1;
    const shapes = new Map(wordCloud.value?.shapeMap);

    try {
        displayLoader(true, 'Uploading shapes...');
        for (const file of files) {
            const image = await uploadImageToS3('preview', file);
            shapes.set(imgPosition, image.Path);
            imgPosition++;
        }
        updateWordCloudConfig({ shapeMap: [...shapes] });
        updateOptionSelected(imgPosition - 1);
    } catch (error) {
        console.error(error);
    } finally {
        displayLoader(false);
    }
};

const removeImageOptionFromMap = (position: number) => {
    const oldMap = new Map(wordCloud.value?.shapeMap);
    oldMap.delete(position);
    updateWordCloudConfig({ shapeMap: [...oldMap] });
    updateOptionSelected(1);
};

const onImageOptionsReordered = async (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.imageElement;
    if (!element) return;

    const shapeMap = new Map<number, string>();

    options.forEach(({ value }, index) => {
        const position = index + 1;
        shapeMap.set(position, (value as DynamicImage).path);
    });

    updateOptionSelected(1);
    updateWordCloudConfig({ shapeMap: [...shapeMap] });
};

const removeImageLibrary = () => {
    updateWordCloudConfig({ libraryId: undefined });
    model.shapeLibraryId = undefined;
    updateOptionSelected(1);
};

const updateWordCloudConfig = (config: Partial<WordCloudConfig>) => {
    const element = props.imageElement;
    if (!element) return;

    if (config.shapeMap) {
        initShapeMap(config.shapeMap);
    }

    const newConfig = { ...wordCloud.value, ...config } as WordCloudConfig;
    element.set('wordCloudConfig', newConfig);
    if (props.editMode) element.setEditedProp('wordCloudConfig');
};

return { __sfc: true,IMAGE_TYPE, props, emit, model, disabled, initShapeMap, updateOptionSelected, wordCloud, displayLoader, onImageUpload, removeImageOptionFromMap, onImageOptionsReordered, removeImageLibrary, updateWordCloudConfig, ElementLibrarySelector }
}

})